// frontend/src/pages/itemPlanningColumns.js
//
// The Item setup grid's two INVENTORY-TARGET override columns:
//
//   inventory_target_mode   target_days_of_supply
//
// Each is the LEAF of the Store → Supplier → Item-location chain
// (api.services.planning_defaults): NULL on the wire means "inherit", and the
// parent a row inherits from is its item's DEFAULT SUPPLIER — or the store,
// when the item has no supplier yet (utils/itemPlanningParent). The cell
// shows the effective value, muted while inherited and in the user ink once
// the row holds its own, exactly as the Supplier setup grid does one level
// up (supplierPlanningColumns.js) — the merchant's question is the same: "is
// this number mine, or the default?".
//
// The parent is named PER ROW. A supplier row always inherits from the store,
// so its inherit option and tooltip are built once per grid; an item row
// inherits from whichever supplier it is sourced from, so the inherit label
// ("Supplier's setting: Days of supply") and the hover ("Supplier Acme:
// 45") are built from the row's nested `default_supplier`. The store's
// defaults (hooks/useStorePlanning) only serve the rows with no supplier;
// until they are known, THOSE rows are read-only — an inherit option cannot
// be offered without naming what it inherits — while rows with a supplier
// edit regardless.
//
// Days of supply is ALWAYS editable and muted under an effective Safety-stock
// mode: the value is stored either way and is what a flip to Days of supply
// will read, so a merchant may set it ahead of the switch. The hover says it
// is not in use.
//
// Extracted from the page so the rules can be pinned without rendering an
// AG Grid (itemPlanningColumns.test.js).

import InheritedCellRenderer from '../components/grid/InheritedCellRenderer';
import { isProductGroupRow } from '../components/grid/productFields';
import {
  INVENTORY_TARGET_MODES,
  isValidTargetDaysOfSupply,
  readsDaysOfSupply,
} from '../utils/inventoryTargetMode';
import { planningParentOf } from '../utils/itemPlanningParent';
import { parseClearableUnitsCell } from '../utils/planningCellParsers';
import { INHERIT, modeLabel, parseChoice } from '../utils/planningInherit';
import { TYPEAHEAD_LIST_PARAMS } from '../utils/referencePickerColDef';

// The two override fields — the page re-derives a row's `effective_*` after
// an edit to either, and after a default-supplier swap (which re-parents them).
export const ITEM_PLANNING_OVERRIDE_FIELDS = ['inventory_target_mode', 'target_days_of_supply'];

const inheritLabel = (t, parent, valueLabel) => t(
  parent.level === 'supplier' ? 'itemSetup.inheritFromSupplier' : 'common.storeSetting',
  { value: valueLabel },
);

// The hover: where the default comes from and what it is, then what a clear
// or the inherit option does — and, on a days cell the effective mode does
// not read, that it is not in use.
function overrideTooltip(t, parent, valueLabel, overridden, howToInherit, notInUse = false) {
  const source = parent.level === 'supplier'
    ? t('itemSetup.supplierValue', { supplier: parent.name, value: valueLabel })
    : t('common.storeSetting', { value: valueLabel });
  const lines = [source, overridden ? t(howToInherit) : t('forecast.clickToOverride')];
  if (notInUse) lines.push(t('common.daysOfSupplyNotRead'));
  return lines.join('\n');
}

/**
 * @param {object} ctx
 * @param {Function} ctx.t
 * @param {(value: number, dp: number) => string} ctx.formatNumber
 * @param {(raw: string) => number} ctx.parseNumber  locale-aware parser.
 * @param {object|null} ctx.planning  the store's defaults
 *   (hooks/useStorePlanning); null while unknown, which leaves the rows with
 *   no default supplier read-only.
 * @returns {object[]} The two columns, in grid order.
 */
export function itemPlanningColumns({ t, formatNumber, parseNumber, planning }) {
  const modeOf = (mode) => modeLabel(t, mode);
  // A grouped grid's product row has no setting, so it inherits from nothing:
  // no hover, no edit.
  const parentOf = (params) => (isProductGroupRow(params.data)
    ? null
    : planningParentOf(params.data.default_supplier, planning));
  const overridden = (field) => (row) => row[field] !== null;
  const daysNotUsed = (row) => !isProductGroupRow(row)
    && !readsDaysOfSupply(row.effective_inventory_target_mode);
  // Editable when the row's parent can be named, so the inherit option and
  // the hover can say what a clear restores.
  const editable = (params) => parentOf(params) !== null;

  return [
    {
      field: 'inventory_target_mode',
      headerName: t('itemSetup.columns.inventoryTargetMode'),
      headerTooltip: t('itemSetup.tooltips.inventoryTargetMode'),
      editable,
      cellEditor: 'agRichSelectCellEditor',
      // Per row: the inherit entry names THIS row's parent value.
      cellEditorParams: (params) => {
        const parent = parentOf(params);
        const inheritOption = inheritLabel(t, parent, modeOf(parent.inventory_target_mode));
        return {
          values: [INHERIT, ...INVENTORY_TARGET_MODES],
          formatValue: (value) => (value === INHERIT || value === null ? inheritOption : modeOf(value)),
          ...TYPEAHEAD_LIST_PARAMS,
        };
      },
      valueSetter: (params) => {
        const parent = parentOf(params);
        if (parent === null) return false;
        const inheritOption = inheritLabel(t, parent, modeOf(parent.inventory_target_mode));
        const parsed = parseChoice(params.newValue, INVENTORY_TARGET_MODES, modeOf, inheritOption);
        if (parsed === undefined) return false;
        params.data.inventory_target_mode = parsed;
        return true;
      },
      cellRenderer: InheritedCellRenderer,
      cellRendererParams: {
        formatValue: modeOf,
        effectiveValue: (row) => row.effective_inventory_target_mode,
        isOverridden: overridden('inventory_target_mode'),
      },
      tooltipValueGetter: (params) => {
        const parent = parentOf(params);
        if (parent === null) return '';
        return overrideTooltip(t, parent, modeOf(parent.inventory_target_mode),
          overridden('inventory_target_mode')(params.data), 'itemSetup.chooseInheritOption');
      },
    },
    {
      field: 'target_days_of_supply',
      headerName: t('itemSetup.columns.daysOfSupply'),
      headerTooltip: t('common.tooltips.daysOfSupply'),
      type: 'numberColumn',
      editable,
      // Blank clears the override (back to the parent's days); a number
      // outside 1–365 is refused in place rather than round-tripped to a 400.
      valueParser: (params) => {
        const parsed = parseClearableUnitsCell(params, parseNumber);
        if (parsed === null) return null;
        return isValidTargetDaysOfSupply(String(parsed)) ? parsed : params.oldValue;
      },
      cellRenderer: InheritedCellRenderer,
      cellRendererParams: {
        formatValue: (value) => formatNumber(value, 0),
        effectiveValue: (row) => row.effective_target_days_of_supply,
        isOverridden: overridden('target_days_of_supply'),
      },
      tooltipValueGetter: (params) => {
        const parent = parentOf(params);
        if (parent === null) return '';
        return overrideTooltip(t, parent, formatNumber(parent.target_days_of_supply, 0),
          overridden('target_days_of_supply')(params.data), 'itemSetup.clearToInherit',
          daysNotUsed(params.data));
      },
      cellClassRules: {
        'ag-cell-not-in-use': (params) => daysNotUsed(params.data),
      },
    },
  ];
}
